import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import styles from "./LicenseCreate.module.css";
import { AxiosInstance } from "../../services/Api";
import {
  fetchCountryOptions,
  fetchStateOptions,
  STATE_TYPES,
  clearStateOptions,
} from "../../redux/Masterdata";
import {
  CustomButton,
  CustomDropdown,
  CustomFileInput,
  CustomTextInput,
} from "../../components";
import PopupModal from "../../components/PopupModal/PopupModal";

// Dropdown options
const accountTypeOptions = [
  { value: "0", label: "Free" },
  { value: "1", label: "Paid" },
  { value: "2", label: "Trial" },
];

const monthsOptions = [
  { value: "1", label: "1" },
  { value: "3", label: "3" },
  { value: "6", label: "6" },
  { value: "12", label: "12" },
  { value: "24", label: "24" },
];

const schoolsOptions = [
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5", label: "5" },
];

// Initial form state
const initialFormState = {
  schoolName: "",
  shortSchoolName: "",
  addressLine1: "",
  addressLine2: "",
  addressLine3: "",
  clientMobileNo: "",
  phoneNumber: "",
  faxNumber: "",
  city: "",
  country: null,
  state: null,
  postalCode: "",
  websiteAddress: "",
  profile_original_name: null,
  profile_upload_path: "",
  accountType: { value: "0", label: "Free" },
  noOfMonths: { value: "1", label: "1" },
  noOfSchools: { value: "1", label: "1" },
  cockpitAdminName: "",
  emailAddress: "",
  cockpitMobileNo: "",
};

const LicenseCreate = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const isEditMode = !!id;

  // Redux state
  const {
    countryOptions: reduxCountryOptions,
    countryLoading,
    fatherStateOptions: reduxStateOptions,
    stateLoading,
  } = useSelector((state) => state.masterData);

  // Form state
  const [formData, setFormData] = useState(initialFormState);

  // Error state
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  // Modal Popup
  const [modalState, setModalState] = useState({
    open: false,
    type: "success", // "success" | "error"
    message: "",
    redirectPath: "",
  });
  // Helper function to find dropdown option by value
  const findDropdownOption = (options, value) => {
    if (!value && value !== 0) return null;
    const stringValue = String(value);
    return options.find((opt) => opt.value === stringValue) || null;
  };

  // Fetch countries on mount
  useEffect(() => {
    dispatch(fetchCountryOptions());
  }, [dispatch]);

  // Fetch states when country changes in form
  useEffect(() => {
    if (formData.country?.value) {
      dispatch(
        fetchStateOptions({
          countryId: formData.country.value,
          stateType: STATE_TYPES.FATHER,
        }),
      );
    } else {
      dispatch(clearStateOptions(STATE_TYPES.FATHER));
    }
  }, [formData.country, dispatch]);

  // Fetch license data for edit mode
  const fetchLicenseData = useCallback(async () => {
    if (!id) return;

    setFetchLoading(true);

    try {
      const response = await AxiosInstance.get(`/licenses/${id}`);

      if (response?.data?.success) {
        const { client_detail, user, client_pricing } = response.data.data;

        const countryOption = findDropdownOption(
          reduxCountryOptions,
          client_detail?.country_id,
        );

        if (client_detail?.country_id) {
          await dispatch(
            fetchStateOptions({
              countryId: String(client_detail.country_id),
              stateType: STATE_TYPES.FATHER,
            }),
          ).unwrap();
        }

        setFormData((prev) => ({
          ...prev,
          schoolName: client_detail?.school_name || "",
          shortSchoolName: client_detail?.short_school_name || "",
          addressLine1: client_detail?.address1 || "",
          addressLine2: client_detail?.address2 || "",
          addressLine3: client_detail?.address3 || "",
          clientMobileNo: client_detail?.client_mobile || "",
          phoneNumber: client_detail?.landline_no || "",
          faxNumber: client_detail?.fax || "",
          city: client_detail?.city || "",
          country: countryOption,
          _pendingStateId: client_detail?.state_id || "",
          postalCode: client_detail?.postal_no
            ? String(client_detail.postal_no)
            : "",
          websiteAddress: client_detail?.web_address || "",
          profile_original_name: client_detail?.photo || null,
          profile_upload_path: client_detail?.group_logo || "",
          accountType: findDropdownOption(
            accountTypeOptions,
            client_pricing?.account_type,
          ) || { value: "0", label: "Free" },
          noOfMonths: findDropdownOption(
            monthsOptions,
            client_pricing?.no_of_months,
          ) || { value: "1", label: "1" },
          noOfSchools: findDropdownOption(
            schoolsOptions,
            client_pricing?.no_of_schools,
          ) || { value: "1", label: "1" },
          cockpitAdminName: client_detail?.cockpit_name || user?.name || "",
          emailAddress: user?.email || "",
          cockpitMobileNo: client_detail?.cockpit_mobile || "",
        }));
      } else {
        throw new Error(
          response?.data?.message || "Failed to fetch license data",
        );
      }
    } catch (error) {
      console.error("Error fetching license:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Error fetching license data";
      alert(errorMessage);
      navigate("/dashboard/license");
    } finally {
      setFetchLoading(false);
    }
  }, [id, navigate, reduxCountryOptions, dispatch]);

  // Set pending state once state options are loaded (for edit mode)
  useEffect(() => {
    if (formData._pendingStateId && reduxStateOptions.length > 0) {
      const stateOption = findDropdownOption(
        reduxStateOptions,
        formData._pendingStateId,
      );
      if (stateOption) {
        setFormData((prev) => ({
          ...prev,
          state: stateOption,
          _pendingStateId: "",
        }));
      }
    }
  }, [reduxStateOptions, formData._pendingStateId]);

  // Fetch license data on mount if edit mode
  useEffect(() => {
    if (isEditMode && reduxCountryOptions.length > 0) {
      fetchLicenseData();
    }
  }, [isEditMode, reduxCountryOptions.length]);

  // Handle input change
  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      const numericFieldsConfig = {
        clientMobileNo: 10,
        phoneNumber: 12,
        postalCode: 6,
        cockpitMobileNo: 10,
      };

      let newValue = value;

      if (numericFieldsConfig.hasOwnProperty(name)) {
        newValue = value.replace(/\D/g, "");
        const maxLength = numericFieldsConfig[name];
        if (newValue.length > maxLength) {
          newValue = newValue.slice(0, maxLength);
        }
      }

      setFormData((prev) => ({
        ...prev,
        [name]: newValue,
      }));

      // ✅ Email validation
      if (name === "emailAddress") {
        newValue = value.trim();

        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/;

        if (newValue && !emailRegex.test(newValue)) {
          setErrors((prev) => ({
            ...prev,
            emailAddress: "Invalid email address",
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            emailAddress: "",
          }));
        }
      }
      // ✅ Fax number validation (Allow typing, show error if invalid)
      if (name === "faxNumber") {
        newValue = value; // DO NOT sanitize

        if (value && /\D/.test(value)) {
          setErrors((prev) => ({
            ...prev,
            faxNumber: "Fax number must contain only numeric values",
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            faxNumber: "",
          }));
        }
      }

      if (errors[name]) {
        setErrors((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    },
    [errors],
  );

  // Handle dropdown change
  const handleDropdownChange = useCallback(
    (name, value) => {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      if (name === "country") {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
          state: null,
        }));
      }

      if (errors[name]) {
        setErrors((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    },
    [errors],
  );

  // Handle file change - CustomFileInput passes file directly, NOT event
  const handleFileChange = useCallback(async (file) => {
    if (!file) {
      setFormData((prev) => ({
        ...prev,
        profile_original_name: null,
        profile_upload_path: "",
      }));
      setErrors((prev) => ({ ...prev, profilePhoto: "" }));
      return;
    }

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];

    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        profilePhoto: "Only jpg, jpeg, png files are allowed",
      }));
      return;
    }

    try {
      const formDataPayload = new FormData();
      formDataPayload.append("school_group", file);

      const response = await AxiosInstance.post(
        "/groupCockpit/upload",
        formDataPayload,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (Array.isArray(response.data) && response.data.length > 0) {
        const uploadedFile = response.data[0];

        setFormData((prev) => ({
          ...prev,
          profile_original_name: uploadedFile.original_name,
          profile_upload_path: uploadedFile.upload_path,
        }));

        setErrors((prev) => ({ ...prev, profilePhoto: "" }));
      } else {
        throw new Error("Invalid upload response");
      }
    } catch (error) {
      console.error("File upload error:", error);
      setErrors((prev) => ({
        ...prev,
        profilePhoto: "Failed to upload file. Please try again.",
      }));
    }
  }, []);

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = {};

    // School Name
    if (!formData.schoolName.trim()) {
      newErrors.schoolName = "School name is required";
    }

    // Short School Name
    if (!formData.shortSchoolName.trim()) {
      newErrors.shortSchoolName = "Short school name is required";
    }

    // Address Line 1
    if (!formData.addressLine1.trim()) {
      newErrors.addressLine1 = "Address Line 1 is required";
    }

    // Client Mobile No — required + exactly 10 digits
    if (!formData.clientMobileNo.trim()) {
      newErrors.clientMobileNo = "Client mobile number is required";
    } else if (formData.clientMobileNo.trim().length < 10) {
      newErrors.clientMobileNo = "Client mobile number must be 10 digits";
    }

    // Phone Number — optional, but if entered must be at least 10 digits
    if (
      formData.phoneNumber.trim() &&
      formData.phoneNumber.trim().length < 10
    ) {
      newErrors.phoneNumber = "Phone number must be at least 10 digits";
    }

    // City
    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }

    // Country
    if (!formData.country) {
      newErrors.country = "Country is required";
    }

    // State
    if (!formData.state) {
      newErrors.state = "State is required";
    }

    // Postal Code
    if (!formData.postalCode.trim()) {
      newErrors.postalCode = "Postal code is required";
    }

    // Website Address — required + must start with https://
    if (!formData.websiteAddress.trim()) {
      newErrors.websiteAddress = "Website address is required";
    } else if (!/^https:\/\/.+/i.test(formData.websiteAddress.trim())) {
      newErrors.websiteAddress =
        "Website must start with https:// (e.g., https://example.com)";
    }

    // Account Type
    if (!formData.accountType) {
      newErrors.accountType = "Account type is required";
    }

    // No of Months
    if (!formData.noOfMonths) {
      newErrors.noOfMonths = "Number of months is required";
    }

    // No of Schools
    if (!formData.noOfSchools) {
      newErrors.noOfSchools = "Number of schools is required";
    }

    // Cockpit Admin Name
    if (!formData.cockpitAdminName.trim()) {
      newErrors.cockpitAdminName = "Cockpit admin name is required";
    }

    // Email Address — required + valid format
    // Email Address
    if (!formData.emailAddress.trim()) {
      newErrors.emailAddress = "Email address is required";
    } else if (/[^a-zA-Z0-9.@]/.test(formData.emailAddress.trim())) {
      newErrors.emailAddress =
        "Sorry, only letters (a-z), numbers (0-9), and periods (.) are allowed";
    } else if (/\.@/.test(formData.emailAddress.trim())) {
      newErrors.emailAddress =
        "Sorry, the last character of your email must be an ascii letter (a-z) or number (0-9)";
    } else if (
      !/^[a-zA-Z0-9.]+@[a-zA-Z0-9.]+\.[a-zA-Z]{2,}$/.test(
        formData.emailAddress.trim(),
      )
    ) {
      newErrors.emailAddress = "Please enter a valid email address";
    }

    // Cockpit Mobile No — required + exactly 10 digits
    if (!formData.cockpitMobileNo.trim()) {
      newErrors.cockpitMobileNo = "Cockpit mobile number is required";
    } else if (formData.cockpitMobileNo.trim().length < 10) {
      newErrors.cockpitMobileNo = "Cockpit mobile number must be 10 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Prepare API payload
  const preparePayload = useCallback(() => {
    return {
      name: formData.cockpitAdminName,
      school_name: formData.schoolName,
      short_school_name: formData.shortSchoolName,
      email: formData.emailAddress,
      cockpit_mobile: formData.cockpitMobileNo,
      client_mobile: formData.clientMobileNo,
      web_address: formData.websiteAddress,
      address1: formData.addressLine1,
      address2: formData.addressLine2 || "",
      address3: formData.addressLine3 || "",
      postal_no: formData.postalCode,
      city: formData.city,
      license_state: formData.state?.value || "",
      country: formData.country?.value || "",
      no_of_months: parseInt(formData.noOfMonths?.value || "1", 10),
      no_of_schools: parseInt(formData.noOfSchools?.value || "1", 10),
      account_type: formData.accountType?.value || "0",
      landline_no: formData.phoneNumber || "",
      fax: formData.faxNumber || "",
      photo: formData.profile_original_name || "",
      path: formData.profile_upload_path || "",
    };
  }, [formData]);

  // Handle form submit
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload = preparePayload();

      let response;

      if (isEditMode) {
        response = await AxiosInstance.put(`/licenses/${id}`, payload);
      } else {
        response = await AxiosInstance.post(`/licenses`, payload);
      }

      if (response?.data?.success) {
        setModalState({
          open: true,
          type: "success",
          message: isEditMode
            ? "License updated successfully!"
            : "License created successfully!",
          redirectPath: "/dashboard/license",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response?.data?.message ||
            `Failed to ${isEditMode ? "update" : "create"} license`,
          redirectPath: "",
        });

        throw new Error(
          response?.data?.message ||
            `Failed to ${isEditMode ? "update" : "create"} license`,
        );
      }
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} license:`,
        error,
      );
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        `Error ${isEditMode ? "updating" : "creating"} license`;
      setModalState({
        open: true,
        type: "error",
        message: errorMessage,
        redirectPath: "",
      });
    } finally {
      setLoading(false);
    }
  }, [validateForm, preparePayload, navigate, isEditMode, id]);

  // Handle reset
  const handleReset = useCallback(() => {
    if (isEditMode) {
      fetchLicenseData();
    } else {
      setFormData(initialFormState);
    }
    setErrors({});
  }, [isEditMode, fetchLicenseData]);

  // Navigate to list
  const handleViewList = useCallback(() => {
    navigate("/dashboard/license");
  }, [navigate]);

  // Show loading state while fetching data
  if (fetchLoading || countryLoading) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.loadingContainer}>
          <i
            className="fa-solid fa-spinner fa-spin"
            style={{ fontSize: "24px" }}
          />
          <span style={{ marginLeft: "10px" }}>Loading license data...</span>
        </div>
      </div>
    );
  }
  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({
      ...prev,
      open: false,
    }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };
  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <div className={styles.pageWrapper}>
        {/* Breadcrumb Header */}
        <div className={styles.breadcrumbHeader}>
          <div className={styles.breadcrumb}>
            <span className={styles.breadcrumbLink}>Home</span>
            <span className={styles.breadcrumbSeparator}>&gt;</span>
            <span className={styles.breadcrumbCurrent}>
              {isEditMode ? "Edit Licence" : "Licence Creation"}
            </span>
          </div>
          <button
            type="button"
            className={styles.viewListButton}
            onClick={handleViewList}
          >
            <i className="fa-solid fa-list" style={{ marginRight: "8px" }} />
            <span>View List</span>
          </button>
        </div>

        {/* Main Form Container */}
        <div className={styles.container}>
          {/* License Creation Section */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {isEditMode ? "Edit License" : "License Creation"}
            </h2>

            <div className="row">
              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label="School Name"
                  name="schoolName"
                  value={formData.schoolName}
                  onChange={handleInputChange}
                  placeholder="Name of the School"
                  required
                  error={errors.schoolName}
                  size="md"
                />
              </div>
              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label="Short School Name"
                  name="shortSchoolName"
                  value={formData.shortSchoolName}
                  onChange={handleInputChange}
                  placeholder="Short Name (e.g., SDA)"
                  required
                  error={errors.shortSchoolName}
                  size="md"
                />
              </div>

              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label="Address Line 1"
                  name="addressLine1"
                  value={formData.addressLine1}
                  onChange={handleInputChange}
                  placeholder="Address Line 1"
                  required
                  error={errors.addressLine1}
                  size="md"
                />
              </div>
              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label="Address Line 2"
                  name="addressLine2"
                  value={formData.addressLine2}
                  onChange={handleInputChange}
                  placeholder="Address Line 2"
                  size="md"
                />
              </div>

              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label="Address Line 3"
                  name="addressLine3"
                  value={formData.addressLine3}
                  onChange={handleInputChange}
                  placeholder="Address Line 3"
                  size="md"
                />
              </div>
              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label="Client Mobile No"
                  name="clientMobileNo"
                  value={formData.clientMobileNo}
                  onChange={handleInputChange}
                  placeholder="Client Mobile No"
                  required
                  error={errors.clientMobileNo}
                  size="md"
                />
              </div>

              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label="Phone Number"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="Landline No (Eg: 044-12345678)"
                  error={errors.phoneNumber}
                  size="md"
                />
              </div>
              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label="Fax Number"
                  name="faxNumber"
                  value={formData.faxNumber}
                  onChange={handleInputChange}
                  placeholder="Fax No (Eg: 070-412 3456)"
                  size="md"
                  error={errors.faxNumber} // ✅ THIS IS REQUIRED
                />
              </div>

              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="City"
                  required
                  error={errors.city}
                  size="md"
                />
              </div>
              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomDropdown
                  label="Country"
                  name="country"
                  value={formData.country}
                  onChange={(value) => handleDropdownChange("country", value)}
                  options={reduxCountryOptions}
                  placeholder="Select Country"
                  required
                  error={errors.country}
                  size="md"
                  isLoading={countryLoading}
                />
              </div>

              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomDropdown
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={(value) => handleDropdownChange("state", value)}
                  options={reduxStateOptions}
                  placeholder="--Please Select--"
                  required
                  error={errors.state}
                  size="md"
                  isLoading={stateLoading}
                />
              </div>
              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label="Postal Code"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  placeholder="Postal Code"
                  required
                  error={errors.postalCode}
                  size="md"
                />
              </div>

              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label="Website Address"
                  name="websiteAddress"
                  value={formData.websiteAddress}
                  onChange={handleInputChange}
                  placeholder="https://example.com"
                  required
                  error={errors.websiteAddress}
                  size="md"
                />
              </div>
              <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                <CustomFileInput
                  label="Group Photo"
                  name="profilePhoto"
                  value={formData.profile_original_name || null}
                  onChange={handleFileChange}
                  accept="image/*"
                  placeholder={
                    formData.profile_original_name
                      ? formData.profile_original_name
                      : "E.g. Mypic.jpg"
                  }
                  buttonText="Choose File"
                />
                {errors.profilePhoto && (
                  <span className={styles.errorText}>
                    {errors.profilePhoto}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Account Details Section */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionHeaderTitle}>
                <i
                  className="fa-solid fa-user-gear"
                  style={{ marginRight: "8px" }}
                />
                Account Details
              </h3>
            </div>

            <div className="row">
              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomDropdown
                  label="Account Type"
                  name="accountType"
                  value={formData.accountType}
                  onChange={(value) =>
                    handleDropdownChange("accountType", value)
                  }
                  options={accountTypeOptions}
                  placeholder="Select Account Type"
                  required
                  error={errors.accountType}
                  size="md"
                />
              </div>
              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomDropdown
                  label="No of Months"
                  name="noOfMonths"
                  value={formData.noOfMonths}
                  onChange={(value) =>
                    handleDropdownChange("noOfMonths", value)
                  }
                  options={monthsOptions}
                  placeholder="Select Months"
                  required
                  error={errors.noOfMonths}
                  size="md"
                />
              </div>
              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomDropdown
                  label="No Of Schools"
                  name="noOfSchools"
                  value={formData.noOfSchools}
                  onChange={(value) =>
                    handleDropdownChange("noOfSchools", value)
                  }
                  options={schoolsOptions}
                  placeholder="Select Schools"
                  required
                  error={errors.noOfSchools}
                  size="md"
                />
              </div>
            </div>
          </div>

          {/* Cockpit Admin Details Section */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionHeaderTitle}>
                <i
                  className="fa-solid fa-user-shield"
                  style={{ marginRight: "8px" }}
                />
                Cockpit Admin Details
              </h3>
            </div>

            <div className="row">
              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label="Cockpit Admin Name"
                  name="cockpitAdminName"
                  value={formData.cockpitAdminName}
                  onChange={handleInputChange}
                  placeholder="Cockpit Admin Name"
                  required
                  error={errors.cockpitAdminName}
                  size="md"
                />
              </div>
              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label="Email Address"
                  name="emailAddress"
                  type="email"
                  value={formData.emailAddress}
                  onChange={handleInputChange}
                  placeholder="Client Email Address"
                  required
                  error={errors.emailAddress}
                  size="md"
                  disabled={isEditMode}
                />
              </div>
              <div className="col-lg-6 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label="Cockpit Mobile No"
                  name="cockpitMobileNo"
                  value={formData.cockpitMobileNo}
                  onChange={handleInputChange}
                  placeholder="Cockpit Admin Mobile No"
                  required
                  error={errors.cockpitMobileNo}
                  size="md"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className={styles.formActions}>
            <CustomButton
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              loadingText={isEditMode ? "Updating..." : "Creating..."}
              fullWidth={false}
              className={styles.submitButton}
            >
              <i
                className={`fa-solid ${isEditMode ? "fa-save" : "fa-plus"}`}
                style={{ marginRight: "8px" }}
              />
              {isEditMode ? "Update License" : "Create License"}
            </CustomButton>
            <CustomButton
              variant="outline-dark"
              onClick={handleReset}
              fullWidth={false}
              className={styles.resetButton}
            >
              <i
                className="fa-solid fa-rotate-left"
                style={{ marginRight: "8px" }}
              />
              Reset
            </CustomButton>
          </div>
        </div>
      </div>
    </>
  );
};

export default LicenseCreate;
